import React from 'react';

import { TriangleAlert } from 'lucide-react';

import { Button } from '@src/components/ui/Button';
import { cn } from '@src/components/ui/utils';


/**
 * Full-bleed error fallback used as the router / CatchBoundary `errorComponent`
 * (see `routes/__root.jsx`, `App.jsx`). Renders a calm danger card —
 * danger-soft tint, hairline border, an alert icon, the error message, and a
 * retry affordance — instead of the old 5-line stub that returned the bare
 * string 'Error' and shadowed the global `Error`.
 *
 * As a TanStack Router error component it receives `{ error, reset }`; `reset`
 * is wired to the retry button. `title` / `description` / `onRetry` let callers
 * reuse it as an inline error state elsewhere.
 */
const ErrorState = React.forwardRef((props, ref) => {
  const {
    error = null,
    reset = null,
    onRetry = null,
    title = 'Something went wrong',
    description = null,
    retryLabel = 'Try again',
    className,
    ...restProps
  } = props;

  const message = description
    || (error && (error.message || String(error)))
    || 'An unexpected error occurred. Please try again.';

  const handleRetry = onRetry || reset;

  return (
    <div
      ref={ref}
      aria-live="assertive"
      className={cn(
        'flex min-h-[240px] w-full items-center justify-center p-6',
        className,
      )}
      role="alert"
      {...restProps}
    >
      <div
        className={cn(
          'flex w-full max-w-md flex-col items-center gap-4 rounded-lg border border-danger/35',
          'bg-danger-soft px-6 py-8 text-center',
        )}
      >
        <span className="grid size-11 place-items-center rounded-full bg-danger/15 text-danger">
          <TriangleAlert className="size-5" aria-hidden="true" />
        </span>

        <div className="flex flex-col gap-1.5">
          <p className="text-[15px] font-semibold text-ink">{title}</p>
          <p className="text-[13px] leading-relaxed text-ink-muted">{message}</p>
        </div>

        {handleRetry
          ? (
            <Button size="sm" variant="secondary" onClick={() => handleRetry()}>
              {retryLabel}
            </Button>
          )
          : null}
      </div>
    </div>
  );
});

ErrorState.displayName = 'ErrorState';

export { ErrorState };
export default ErrorState;
